import React, { useState, useEffect } from 'react';
import { RiCloseCircleLine } from "react-icons/ri";
import { TooltipComponent } from '@syncfusion/ej2-react-popups';
import { useStateContext } from '../contexts/ContextProvider';
import axios from 'axios';
import moment from 'moment';
import { FaWhatsapp  } from "react-icons/fa";
import LoadingOverlay from './LoadingOverlay';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const NavButton = ({ title, customFunc, icon, color, size = 'xl', padding = 'p-3' }) => (
  <TooltipComponent content={title} position="BottomCenter">
    <button
      type="button"
      onClick={customFunc}
      style={{ color }}
      className={`relative text-${size} rounded-full ${padding} hover:bg-light-gray`}
    >
      {icon}
    </button>
  </TooltipComponent>
);

const simulateFileToBase64 = async (fileName) => {
  try {
    // La ruta asume que las imágenes están en la carpeta 'public/images'
    const response = await fetch(`/images/${fileName}`);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]); // Devuelve solo el contenido base64
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error(`Error al convertir el archivo ${fileName} a Base64:`, error);
    return null;
  }
};

const DetalleCotizacion = ({ cotizacionId, onClose,onAprobar  }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const { currentColor } = useStateContext();
  const [cotizacion, setCotizacion] = useState(null); // Estado para almacenar los detalles de la cotización
  const [cliente, setCliente] = useState(null); // Estado para almacenar los detalles del cliente
  const [loading, setLoading] = useState(true); // Estado de carga
  const [error, setError] = useState(null); // Estado para manejar errores
  const [fechaCotizacion, setFechaCotizacion] = useState('');
  const [totalM2, setTotalM2] = useState(0);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 768);

  const getImageFileName = (itemName) => {
    const mappings = {
      optivision: "OptivisionNeutra.png",
      optivisión: "OptivisionNeutra.png",
      blackpearl: "PremiumBlackPearl.png",
      "nova 70": "PremiumNova70.png",
      purelite: "PremiumPureLite.jpg",
      plata: "SilverPlataEspejo.png",
    };
  
    // Busca una coincidencia en las claves del objeto `mappings`
    const matchedKey = Object.keys(mappings).find((key) =>
      itemName.toLowerCase().includes(key)
    );
  
    // Devuelve el nombre del archivo o `null` si no hay coincidencia
    return matchedKey ? mappings[matchedKey] : null;
  };
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 768);
    };
  
    window.addEventListener('resize', handleResize);
  
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!cotizacionId) {
      return; // No intentes cargar si el ID es null
    }
    let isMounted = true; // Para controlar si el componente sigue montado
    // Limpiar estado de error y cliente al abrir el modal
    setError(null);
    setCliente(null);
  
    const fetchCotizacion = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cotizaciones/${cotizacionId}/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (isMounted) {
          setCotizacion(response.data);
          // Formatear la fecha y guardarla
          const formattedDate = new Date(response.data.fecha_cotizacion).toLocaleDateString('es-CL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          });
          setFechaCotizacion(formattedDate);
    
          if (response.data.solicitante) {
            const clienteResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/clientes/clientes/${response.data.solicitante}/`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setCliente(clienteResponse.data); // Asignar el objeto cliente directamente
          }
                  // Calcula el total de metros cuadrados sumando el campo m2 de cada detalle
        const totalMetros = response.data.detalles.reduce((total, item) => {
          return total + (parseFloat(item.m2) || 0); // Asegúrate de que m2 sea un número válido
        }, 0);
        setTotalM2(totalMetros); // Guarda el total de m² en el estado
        setLoading(false);
        }
        console.error("error",error)
      } catch (error) {
        console.error('Error al cargar los detalles de la cotización o cliente:', error);
        if (isMounted) {
          setError('No se pudo cargar la cotización o el cliente.');
          setLoading(false);
        }
      }
    };
  
    fetchCotizacion();
  
    return () => {
      isMounted = false;
    };
  }, [cotizacionId]);

  if (loading) {
    return <div>Cargando los detalles de la cotización...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  const buildPDF = async () => {
    // Logo
    const logoRes = await fetch('/ISOLOGOTIPO_NARANJA_BLANCO.png');
    const logoBlob = await logoRes.blob();
    const logoDataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(logoBlob);
    });
    const logoImg = new Image();
    await new Promise((resolve, reject) => {
      logoImg.onload = resolve;
      logoImg.onerror = reject;
      logoImg.src = logoDataUrl;
    });
    const logoAspectRatio = logoImg.naturalWidth / logoImg.naturalHeight;

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = { left: 15, right: 15, bottom: 20 };

    const headerBg    = [37, 109, 159];
    const accentColor = [255, 116, 58];
    const textColor   = [60, 60, 60];
    const mutedColor  = [120, 120, 120];
    const lightGray   = [245, 245, 245];

    const fmt = (v) => Number(v).toLocaleString('es-CL', { style: 'currency', currency: 'CLP' });

    // ── HEADER ──
    const headerH = 32;
    doc.setFillColor(...headerBg);
    doc.rect(0, 0, pageWidth, headerH, 'F');

    const logoH = 14;
    const logoW = logoH * logoAspectRatio;
    doc.addImage(logoDataUrl, 'PNG', margin.left, (headerH - logoH) / 2, logoW, logoH);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(255, 255, 255);
    doc.text(`Cotización N° ${cotizacion.id_cotizacion}`, pageWidth - margin.right, 14, { align: 'right' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Fecha: ${fechaCotizacion}`, pageWidth - margin.right, 21, { align: 'right' });

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text((cotizacion.estado || 'pendiente').toUpperCase(), pageWidth - margin.right, 27, { align: 'right' });

    let y = headerH + 10;

    // ── DATOS CLIENTE / PROYECTO ──
    const colL = margin.left;
    const colR = pageWidth / 2 + 5;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textColor);
    doc.text('DATOS DEL CLIENTE', colL, y);
    doc.text('DATOS DEL PROYECTO', colR, y);

    y += 6;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const nombreCompleto = cliente ? `${cliente.nombre} ${cliente.apellido}` : '-';
    doc.text(nombreCompleto, colL, y);
    doc.text(`Servicio: ${cotizacion.tipo_instalacion || '-'}`, colR, y);
    y += 5;

    doc.text(cliente?.email || '-', colL, y);
    doc.text(`Dificultad: ${cotizacion.dificultad || '-'}`, colR, y);
    y += 5;

    doc.text(cliente?.whatsapp || '-', colL, y);
    doc.text(`Termopanel: ${cotizacion.termopanel || '-'}`, colR, y);
    y += 5;

    const dir = [cotizacion.direccion_instalacion, cliente?.comuna].filter(Boolean).join(' - ');
    doc.text(dir || '-', colL, y);
    doc.text(`Retiro lámina: ${cotizacion.retiro_laminas ? 'Sí' : 'No'}`, colR, y);

    y += 10;

    // ── TABLA ──
    const grouped = cotizacion.detalles.reduce((acc, item) => {
      const key = `${item.item}-${item.alto}-${item.ancho}-${item.m2}-${item.total}`;
      if (!acc[key]) acc[key] = { ...item, cantidad: 1 };
      else acc[key].cantidad += 1;
      return acc;
    }, {});
    const groupedArr = Object.values(grouped);

    const tableRows = groupedArr.map(item => {
      const m2Tot = (parseFloat(item.m2) || 0) * item.cantidad;
      const priceTot = (parseFloat(item.total) || 0) * item.cantidad;
      return [item.item, `${item.alto || 'N/A'} x ${item.ancho || 'N/A'}`, item.cantidad, `${m2Tot.toFixed(2)} m²`, fmt(item.precio), fmt(priceTot)];
    });

    if (cotizacion.retiro_laminas)
      tableRows.push(['Retiro de láminas', '', '', '', '', fmt(cotizacion.monto_retiro_laminas)]);
    if (cotizacion.cobro_distancia)
      tableRows.push(['Monto por distancia', '', '', '', '', fmt(cotizacion.monto_distancia)]);
    if (cotizacion.dificultad !== 'normal')
      tableRows.push([`Dificultad: ${cotizacion.dificultad}`, '', '', '', '', fmt(cotizacion.monto_dificultad)]);

    doc.autoTable({
      startY: y,
      margin: { left: margin.left, right: margin.right },
      head: [['Ítem', 'Medidas', 'Cant.', 'M²', 'Precio m²', 'Total']],
      body: tableRows,
      styles: { fontSize: 9, textColor },
      headStyles: { fillColor: accentColor, textColor: 255, fontStyle: 'bold' },
      columnStyles: { 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'right' }, 5: { halign: 'right' } },
    });

    y = doc.lastAutoTable.finalY + 10;

    // ── PAGO + TOTALES ──
    const sectionY = y;

    const bankData = [
      ['Nombre',         process.env.REACT_APP_NOMBRE_PAGO        || 'Film Solar Spa'],
      ['RUT',            process.env.REACT_APP_RUT_PAGO            || '78.025.351-7'],
      ['Banco',          process.env.REACT_APP_BANCO_PAGO          || 'Banco Santander'],
      ['Tipo de cuenta', process.env.REACT_APP_TIPO_CUENTA_PAGO    || 'Cuenta Corriente'],
      ['N° cuenta',      process.env.REACT_APP_NUMERO_CUENTA_PAGO  || '0-000-9629135-3'],
      ['Email de pago',  process.env.REACT_APP_MAIL_CUENTA_PAGO    || 'PagosFilmsolar@gmail.com'],
    ];

    const payW = 90;
    const payBoxH = bankData.length * 6 + 12;
    doc.setFillColor(...lightGray);
    doc.roundedRect(colL, y - 3, payW, payBoxH, 2, 2, 'F');

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...mutedColor);
    doc.text('INFORMACIÓN DE PAGO', colL + 4, y + 3);

    y += 9;
    bankData.forEach(([label, value]) => {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...mutedColor);
      const ls = label + ': ';
      doc.text(ls, colL + 4, y);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...textColor);
      doc.text(value, colL + 4 + doc.getTextWidth(ls), y);
      y += 6;
    });

    const payEndY = sectionY - 3 + payBoxH + 8;
    y = sectionY;

    // Totales
    const subtotalItems = cotizacion.detalles.reduce((s, d) => s + (parseFloat(d.total) || 0), 0);
    const cargoRetiro     = cotizacion.retiro_laminas    ? (parseFloat(cotizacion.monto_retiro_laminas) || 0) : 0;
    const cargoDistancia  = cotizacion.cobro_distancia   ? (parseFloat(cotizacion.monto_distancia)       || 0) : 0;
    const cargoDificultad = cotizacion.dificultad !== 'normal' ? (parseFloat(cotizacion.monto_dificultad) || 0) : 0;
    const subtotal = subtotalItems + cargoRetiro + cargoDistancia + cargoDificultad;
    const ivaTotal = Math.round(subtotal * 0.19);
    const descuento = Math.round(subtotal * 1.19 - cotizacion.total_cotizacion);

    const totBoxX = pageWidth - margin.right - 70;
    const totBoxW = 70;
    const grayH = descuento > 0 ? 30 : 24;
    const vX = pageWidth - margin.right - 5;

    doc.setFillColor(...lightGray);
    doc.roundedRect(totBoxX, y - 3, totBoxW, grayH, 2, 2, 'F');

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...textColor);

    doc.text('Subtotal',   totBoxX + 5, y + 2); doc.text(fmt(subtotal), vX, y + 2, { align: 'right' }); y += 6;
    doc.text('IVA (19%)', totBoxX + 5, y + 2); doc.text(fmt(ivaTotal), vX, y + 2, { align: 'right' }); y += 6;
    doc.text('Total m²',  totBoxX + 5, y + 2); doc.text(`${totalM2.toFixed(2)} m²`, vX, y + 2, { align: 'right' }); y += 6;

    if (descuento > 0) {
      const dLabel = cotizacion.campaign_info ? `Dcto. ${cotizacion.campaign_info.codigo}` : 'Descuento';
      doc.setTextColor(34, 139, 34);
      doc.text(dLabel, totBoxX + 5, y + 2);
      doc.text(`-${fmt(descuento)}`, vX, y + 2, { align: 'right' });
      doc.setTextColor(...textColor);
      y += 6;
    }

    const totH = 24;
    doc.setFillColor(...accentColor);
    doc.roundedRect(totBoxX, y, totBoxW, totH, 3, 3, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('TOTAL', totBoxX + 5, y + 8);
    doc.setFontSize(14);
    doc.text(fmt(cotizacion.total_cotizacion), vX, y + 8, { align: 'right' });

    y = Math.max(y + totH + 10, payEndY);

    // ── TÉRMINOS ──
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textColor);
    doc.text('Términos y Condiciones', margin.left, y);
    y += 6;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const terminos = [
      '1. Vigencia de la Cotización: 30 días desde su emisión.',
      '2. Condiciones de Pago: Abono mínimo requerido para reservar la instalación.',
      '3. Agenda: Se realiza entre 3 a 7 días hábiles después del abono.',
      '4. Garantía: Cubre desprendimiento de lámina, burbujas, descoloramiento y defectos de instalación.',
      '5. Exclusiones de Garantía: No incluye reposición de vidrios por fisuras de estrés térmico.',
      '6. Alcance del Servicio: Incluye láminas e instalación, con vida útil de hasta 10 años.',
    ];
    terminos.forEach(t => { doc.text(t, margin.left, y); y += 4; });

    // ── FOOTER ──
    const footerY = pageHeight - margin.bottom - 12;
    doc.setDrawColor(...mutedColor);
    doc.setLineWidth(0.3);
    doc.line(margin.left, footerY, pageWidth - margin.right, footerY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...mutedColor);
    doc.text('+56 9 66781957 | 12 Norte 785 of. 401 Edificio Pamplona | www.filmsolar.cl', pageWidth / 2, footerY + 6, { align: 'center' });

    return doc;
  };

  const handleDownloadPDF = async () => {
    try {
      setLoadingMessage('Generando PDF...');
      setIsProcessing(true);
      const doc = await buildPDF();
      doc.save(`Cotizacion_${cotizacion.id_cotizacion}.pdf`);
    } catch (error) {
      alert('Hubo un problema al generar el PDF.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendPDFByEmail = async () => {
    try {
      setLoadingMessage('Enviando PDF por correo...');
      setIsProcessing(true);

      const doc = await buildPDF();
      const pdfBase64 = await new Promise(resolve => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(doc.output('blob'));
      });

      const token = localStorage.getItem('access_token');
      const destinatario = cliente.email;
      const asunto = `FilmSolar - Cotización #${cotizacionId}`;
      const mensaje = `
        <p>Estimado/a <strong>${cliente.nombre} ${cliente.apellido}</strong>,</p>
        <p>Adjunto encontrará el PDF de la <strong>cotización #${cotizacionId}</strong>.</p>
        <p>Detalles de la cotización:</p>
        <ul>
          <li><strong>Fecha de cotización: </strong>${fechaCotizacion}</li>
          <li><strong>Total:</strong> ${cotizacion.total_cotizacion.toLocaleString('es-CL', { style: 'currency', currency: 'CLP' })}</li>
          <li><strong>Tipo de instalación:</strong> ${cotizacion.tipo_instalacion}</li>
        </ul>
        <p>Si tiene alguna consulta, no dude en ponerse en contacto con nosotros.</p>
        <p>Saludos cordiales,</p>
        <p><strong>El equipo de FilmSolar</strong></p>
        <p>Correo: <a href="mailto:${process.env.REACT_APP_MAIL_CUENTA_PAGO}">${process.env.REACT_APP_MAIL_CUENTA_PAGO}</a></p>
      `;

      const archivos = await Promise.all(
        cotizacion.detalles.map(async (item) => {
          const fileName = getImageFileName(item.item);
          if (!fileName) return null;
          const base64Content = await simulateFileToBase64(fileName);
          if (!base64Content) return null;
          return {
            contenido: base64Content,
            nombre: fileName,
            tipo: fileName.endsWith('.png') ? 'image/png' : 'image/jpeg',
          };
        })
      ).then(results => results.filter(Boolean));

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/correo/enviar-correo/`,
        {
          destinatario,
          asunto,
          mensaje,
          archivos: [
            ...archivos,
            { contenido: pdfBase64, nombre: `Cotizacion_${cotizacionId}.pdf`, tipo: 'application/pdf' },
          ],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('El PDF se ha enviado correctamente por correo.');
    } catch (error) {
      alert('Error al enviar el PDF por correo.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleChangeEstado = async (nuevoEstado) => {
    try {
        setLoadingMessage("Actualizando estado de la cotización...");
        setIsProcessing(true);
        const token = localStorage.getItem('access_token');
        
        // Primero, cambia el estado de la cotización
        await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/cotizaciones/${cotizacionId}/cambiar-estado/`, 
        { estado: nuevoEstado }, 
        {
            headers: { Authorization: `Bearer ${token}` }
        });

        // Actualiza el estado localmente para reflejar el cambio
        setCotizacion((prevCotizacion) => ({
            ...prevCotizacion,
            estado: nuevoEstado
        }));
      console.error("validamos estado 2",error)
      // Si el estado es `aprobado`, crea la orden de venta y cierra el modal
      if (nuevoEstado === 'aprobado') {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/ordenes/crear-orden-desde-cotizacion/`,
          { cotizacion_id: cotizacionId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.error("validamos estado 3:",error)
        // Llamar a la función onAprobar para cerrar el modal y refrescar la grilla
        if (onAprobar) {
          onAprobar();
        }
        // Cierra el modal automáticamente
        if (onClose) {
          onClose();
        }
        // Actualiza el estado localmente
        setCotizacion((prev) => ({
          ...prev,
          estado: nuevoEstado,
        }));
      } else {
        // Actualiza el estado localmente para reflejar el cambio
        setCotizacion((prevCotizacion) => ({
          ...prevCotizacion,
          estado: nuevoEstado,
        }));
      }
      console.error("validamos estado 4:",error)
    } catch (error) {
    alert('Ocurrió un error al cambiar el estado.');
  } finally {
    setIsProcessing(false);
  }
};


  const handleAprobarClick = () => {
    const confirmar = window.confirm('¿Estás seguro de que deseas aprobar esta cotización?');
    if (confirmar) {
      handleChangeEstado('aprobado');
    }
    console.error("fin del handle:",error)
  };

  // Cálculo del descuento real en CLP (compartido entre el bloque de campaña y la tabla)
  const _subtotalBase = cotizacion.detalles.reduce((s, d) => s + (parseFloat(d.total) || 0), 0)
    + (cotizacion.retiro_laminas ? (parseFloat(cotizacion.monto_retiro_laminas) || 0) : 0)
    + (cotizacion.cobro_distancia ? (parseFloat(cotizacion.monto_distancia) || 0) : 0)
    + (cotizacion.dificultad !== 'normal' ? (parseFloat(cotizacion.monto_dificultad) || 0) : 0);
  const montoDescuentoCLP = Math.round(_subtotalBase * 1.19 - cotizacion.total_cotizacion);

  return (
    <>
      <div className="bg-half-transparent w-screen fixed nav-item top-0 right-0 h-full flex">
        
      <div className="responsive-div">
          <div className="flex justify-between items-center mb-6">
            <p className="font-semibold text-lg" style={{ color: '#FF743A' }}>Detalle Cotización</p>
            <NavButton
              title="Cerrar"
              customFunc={onClose}
              color={currentColor}
              icon={<RiCloseCircleLine />}
              size="3xl"  // Aumenta el tamaño del ícono
              padding="p-4"  // Aumenta el padding del botón
            />
          </div>

          <div className="cotizacion-header">
            <div className="yellow-bar-left"></div>
            <h1 className="cotizacion-title">COTIZACIÓN {cotizacion.id_cotizacion}</h1>
            <div className="yellow-bar-right"></div>
          </div>

          {/* Información del cliente */}
          {cliente && (
            <div
              className={`cliente-info ${isSmallScreen ? "flex flex-col" : "flex flex-row justify-between"}`}
            >
              <div className={`${isSmallScreen ? "" : "left-info"} mb-4`}>
                <p><strong>Cliente: </strong>{cliente.nombre} {cliente.apellido}</p>
                <p className="direccion-text">
                  <strong>Dirección: </strong>{cotizacion.direccion_instalacion} - {cliente.comuna}
                </p>
              </div>
              <div className={`${isSmallScreen ? "" : "right-info"}`}>
                <p><strong>Teléfono/Wsp: </strong>{cliente.whatsapp}</p>
                <p><strong>Email: </strong><span>{cliente.email}</span></p>
              </div>
            </div>
          )}
          {cliente && (
            <div className="cliente-info">
              <div className="left-info">
                <p><strong>Estado:</strong> {cotizacion.estado}</p>
                <p><strong>Fecha Cotización:</strong> {moment(cotizacion.fecha_cotizacion).format('DD/MM/YYYY')}</p>
                <p><strong>Tipo Servicio:</strong> {cotizacion.tipo_instalacion}</p>
                <p><strong>Vendedor:</strong> {cotizacion.vendedor?.username || "No asignado"}</p>
              </div>
              <div className="right-info">
                <p><strong>Retiro Lamina:</strong> {cotizacion.retiro_laminas ? "Sí" : "No"}</p>
                <p><strong>Termopanel:</strong> {cotizacion.termopanel}</p>
                <p><strong>Dificultad:</strong> {cotizacion.dificultad}</p>
                <p><strong>Tipo Propiedad:</strong> {cotizacion.tipo_propiedad}</p>
              </div>
            </div>
          )}




          {cotizacion.campaign_info && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-4 mx-1">
              <p className="text-sm font-semibold text-orange-700 mb-1">Origen: Campaña de mailing</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <p className="text-xs text-gray-600"><strong>Campaña:</strong> {cotizacion.campaign_info.campaign_nombre}</p>
                <p className="text-xs text-gray-600"><strong>Código:</strong> {cotizacion.campaign_info.codigo}</p>
                <p className="text-xs text-gray-600"><strong>Destinatario:</strong> {cotizacion.campaign_info.email_destinatario}</p>
                <p className="text-xs text-gray-600">
                  <strong>Descuento:</strong>{' '}
                  {cotizacion.campaign_info.valor_descuento}{cotizacion.campaign_info.tipo_descuento === 'porcentaje' ? '%' : ' CLP'}
                  {montoDescuentoCLP > 0 && (
                    <span className="text-green-700 font-semibold ml-1">
                      (−{montoDescuentoCLP.toLocaleString('es-CL', { style: 'currency', currency: 'CLP' })})
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}

          <div className="overflow-x-auto w-full" style={{ minWidth: 0 }}>
          <table className="items-table table-auto border-collapse w-full">
            <thead>
            <tr className="bg-gray-100">
                <th>Nº</th>
                <th>Item</th>
                <th>Precio (m²)</th>
                <th>Cant.</th>
                {isSmallScreen ? (
                  <th>Medidas</th>
                ) : (
                  <>
                    <th>Alto</th>
                    <th>Ancho</th>
                  </>
                )}
                <th>M²</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Agrupa los detalles por columnas clave
                const groupedDetails = cotizacion.detalles.reduce((acc, item) => {
                  const key = `${item.item}-${item.alto}-${item.ancho}-${item.m2}-${item.total}`;
                  if (!acc[key]) {
                    acc[key] = { ...item, cantidad: 1 };
                  } else {
                    acc[key].cantidad += 1;
                  }
                  return acc;
                }, {});
                console.error("groupedDetails",Object.values(groupedDetails))
                // Convierte el objeto agrupado en un array para iterar
                const groupedArray = Object.values(groupedDetails);

                // Renderizar filas agrupadas
                const rows = groupedArray.map((item, index) => {
                  const totalM2 = (parseFloat(item.m2) || 0) * item.cantidad;
                  const totalPrecio = (parseFloat(item.total) || 0) * item.cantidad;

                  return (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.item}</td>
                      <td>{(item.precio / 1).toLocaleString('es-CL', { style: 'currency', currency: 'CLP' })}</td>
                      <td>{item.cantidad}</td>
                {isSmallScreen ? (
                  <td>{`${item.alto || 'N/A'} x ${item.ancho || 'N/A'}`}</td>
                ) : (
                  <>
                    <td>{item.alto || 'N/A'}</td>
                    <td>{item.ancho || 'N/A'}</td>
                  </>
                )}
                      <td>{totalM2 ? `${totalM2.toFixed(2)}` : 'N/A'}</td>
                      <td>{totalPrecio ? `$${totalPrecio.toLocaleString()}` : 'N/A'}</td>
                    </tr>
                  );
                });

                // Agregar filas adicionales
                let extraRowIndex = groupedArray.length;
                if (cotizacion.retiro_laminas) {
                  rows.push(
                    <tr key="retiro_laminas">
                      <td>{++extraRowIndex}</td>
                      {isSmallScreen ? (
                        <td colSpan="5">Retiro de láminas</td>
                      ) : (
                        <td colSpan="6">Retiro de láminas</td>
                      )}
                      <td>{`$${parseFloat(cotizacion.monto_retiro_laminas).toLocaleString()}`}</td>
                    </tr>
                  );
                }

                if (cotizacion.cobro_distancia) {
                  rows.push(
                    <tr key="cobro_distancia">
                      <td>{++extraRowIndex}</td>
                      {isSmallScreen ? (
                        <td colSpan="5">Monto por distancia</td>
                      ) : (
                        <td colSpan="6">Monto por distancia</td>
                      )}
                      <td>{`${(cotizacion.monto_distancia).toLocaleString('es-CL', { style: 'currency', currency: 'CLP' })}`}</td>
                    </tr>
                  );
                }

                if (cotizacion.dificultad !== 'normal') {
                  rows.push(
                    <tr key="dificultad">
                      <td>{++extraRowIndex}</td>
                      {isSmallScreen ? (
                        <td colSpan="5">{`Dificultad: ${cotizacion.dificultad}`}</td>
                      ) : (
                        <td colSpan="6">{`Dificultad: ${cotizacion.dificultad}`}</td>
                      )}
                      <td>{`${parseFloat(cotizacion.monto_dificultad).toLocaleString('es-CL', { style: 'currency', currency: 'CLP' })}`}</td>
                    </tr>
                  );
                }

                // Subtotal = suma de todos los ítems + cargos adicionales
                const subtotalItems = cotizacion.detalles.reduce((sum, d) => sum + (parseFloat(d.total) || 0), 0);
                const cargoRetiro = cotizacion.retiro_laminas ? (parseFloat(cotizacion.monto_retiro_laminas) || 0) : 0;
                const cargoDistancia = cotizacion.cobro_distancia ? (parseFloat(cotizacion.monto_distancia) || 0) : 0;
                const cargoDificultad = cotizacion.dificultad !== 'normal' ? (parseFloat(cotizacion.monto_dificultad) || 0) : 0;
                const subtotal = subtotalItems + cargoRetiro + cargoDistancia + cargoDificultad;

                // IVA sobre el subtotal real (no desde total_cotizacion)
                const ivaTotal = subtotal * 0.19;

                // Descuento = diferencia entre (subtotal + IVA) y el total final
                const montoDescuento = Math.round(subtotal * 1.19 - cotizacion.total_cotizacion);

                // Fila subtotal
                rows.push(
                  <tr key="subtotal" className="border-t-2 border-gray-200">
                    <td></td>
                    {isSmallScreen ? (
                      <td colSpan="5" className="font-semibold text-gray-700">Subtotal</td>
                    ) : (
                      <td colSpan="6" className="font-semibold text-gray-700">Subtotal</td>
                    )}
                    <td className="font-semibold">{subtotal.toLocaleString('es-CL', { style: 'currency', currency: 'CLP' })}</td>
                  </tr>
                );

                // Fila IVA
                rows.push(
                  <tr key="iva">
                    <td></td>
                    {isSmallScreen ? (
                      <td colSpan="5">IVA (19%)</td>
                    ) : (
                      <td colSpan="6">IVA (19%)</td>
                    )}
                    <td>{ivaTotal.toLocaleString('es-CL', { style: 'currency', currency: 'CLP' })}</td>
                  </tr>
                );

                // Fila descuento (campaña o regular)
                if (montoDescuento > 0) {
                  const descuentoLabel = cotizacion.campaign_info
                    ? `Descuento campaña ${cotizacion.campaign_info.codigo} (${cotizacion.campaign_info.valor_descuento}${cotizacion.campaign_info.tipo_descuento === 'porcentaje' ? '%' : ' CLP'})`
                    : `Descuento ${parseFloat(cotizacion.descuento).toLocaleString()}%`;
                  rows.push(
                    <tr key="descuento">
                      <td></td>
                      {isSmallScreen ? (
                        <td colSpan="5" className="text-green-700 font-medium">{descuentoLabel}</td>
                      ) : (
                        <td colSpan="6" className="text-green-700 font-medium">{descuentoLabel}</td>
                      )}
                      <td className="text-green-700 font-medium">-{montoDescuento.toLocaleString('es-CL', { style: 'currency', currency: 'CLP' })}</td>
                    </tr>
                  );
                }
                return rows;
              })()}
            </tbody>
          </table>
          </div>

          {/* Monto total debajo de la tabla */}
          <div className="flex justify-end mt-4">
            <p className="font-semibold text-lg">Total: {(cotizacion.total_cotizacion / 1).toLocaleString('es-CL', { style: 'currency', currency: 'CLP' })}</p>
          </div>

          {/* Monto total de m2 debajo de la tabla */}
          <div className="flex justify-end mt-2">
            <p className="font-semibold text-lg">Total m²: {totalM2.toFixed(2)} m²</p>
          </div>


          <div className="flex flex-col md:flex-row justify-between mt-4 space-y-2 md:space-y-0 md:space-x-2">
            <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2">
            <button onClick={handleDownloadPDF} type="button" 
            className="bg-[#FF743A] text-white p-2 rounded-lg hover:bg-[#e8632e] transition duration-200 w-full md:w-auto"
            >
              Descargar
            </button>
            <button onClick={handleSendPDFByEmail} type="button" className="bg-[#FF743A] text-white p-2 rounded-lg hover:bg-red-600 transition duration-200 w-full md:w-auto">
              Enviar por Email

            </button>

              <a
              href={
                cliente
                  ? `https://web.whatsapp.com/send?phone=${cliente.whatsapp.replace(/[^0-9]/g, '')}&text=${encodeURIComponent(
                      `Estimado Sr. ${cliente.nombre} ${cliente.apellido},

              Adjunto encontrará la cotización N°${cotizacionId}, junto con la ficha técnica del film, el cual cumple con todas las características solicitadas.

              Detalles de la cotización:
              - Metros cuadrados (m²): ${totalM2.toFixed(2)}
              - Total: $${cotizacion.total_cotizacion.toLocaleString('es-CL')}
              - Tipo de servicio: ${cotizacion.tipo_instalacion}

              Ante cualquier consulta adicional, quedamos atentos para responder a la brevedad.

              Saludos cordiales,
              Equipo FilmSolar`
                    )}`
                  : '#'
              }
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center justify-center bg-green-500 text-white p-2 rounded-lg ${
                cliente ? 'hover:bg-green-600' : 'cursor-not-allowed'
              } transition duration-200 ml-2`}
              onClick={(e) => {
                if (!cliente) {
                  e.preventDefault();
                  alert('Los datos del cliente aún no están disponibles.');
                }
              }}
              >
              <FaWhatsapp className="mr-2" /> Enviar por Wsp
              </a>
            </div>


            
            <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2">
              {cotizacion.estado === 'pendiente' && (
                <>
                  <button
                    type="button"
                    className="bg-green-500 text-white p-2 rounded-lg hover:bg-green-600 transition duration-200 w-full md:w-auto"
                    onClick={handleAprobarClick}
                  >
                    Aprobar
                  </button>
                  <button
                    type="button"
                    className="bg-red-500 text-white p-2 rounded-lg hover:bg-red-600 transition duration-200 w-full md:w-auto"
                    onClick={() => handleChangeEstado('rechazado')}
                  >
                    Rechazar
                  </button>
                </>
              )}
            </div>
          </div>
      </div>
      </div>
      {isProcessing && <LoadingOverlay message={loadingMessage} />}
    </>
  );
};

export default DetalleCotizacion;
